import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Database, Thermometer, Mountains, Fire, Wind, Drop, Leaf,
  MagnifyingGlass, MapPin, CalendarBlank, ChartLineUp, ChartBar as ChartBarIcon,
  DownloadSimple, FileText, FileCsv, Crosshair, CircleNotch, CheckCircle, Eye,
} from "@phosphor-icons/react";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATA_TYPES = [
  { id: "temperature", label: "Temperature", unit: "°C", Icon: Thermometer, color: "#ef4444", bg: "bg-red-500/15" },
  { id: "earthquake", label: "Earthquakes", unit: "Magnitude", Icon: Mountains, color: "#f97316", bg: "bg-orange-500/15" },
  { id: "wildfire", label: "Wildfires", unit: "Hotspots", Icon: Fire, color: "#dc2626", bg: "bg-red-600/15" },
  { id: "aqi", label: "Air Quality", unit: "AQI", Icon: Wind, color: "#8b5cf6", bg: "bg-violet-500/15" },
  { id: "rainfall", label: "Rainfall", unit: "mm", Icon: Drop, color: "#3b82f6", bg: "bg-blue-500/15" },
  { id: "ndvi", label: "Vegetation", unit: "NDVI", Icon: Leaf, color: "#10b981", bg: "bg-emerald-500/15" },
];

const PRESETS = [
  { label: "Last 7 days", days: 7 }, { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 }, { label: "Last Year", days: 365 },
];

function daysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

export default function DataCenterPage() {
  const { user } = useAuth();
  const [dataType, setDataType] = useState("temperature");
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(daysAgo(1));
  const [lat, setLat] = useState("35.6762");
  const [lon, setLon] = useState("139.6503");
  const [locationName, setLocationName] = useState("Tokyo, Japan");
  const [searchQuery, setSearchQuery] = useState("");
  const [forecast, setForecast] = useState(true);
  const [chartType, setChartType] = useState("line");
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const activeType = DATA_TYPES.find((t) => t.id === dataType);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await axios.get(`${API}/geocode`, { params: { q: searchQuery } });
      const loc = res.data?.results?.[0];
      if (loc) {
        setLat(loc.lat.toFixed(4));
        setLon(loc.lon.toFixed(4));
        setLocationName(loc.name.split(",").slice(0, 2).join(", "));
        setSearchQuery("");
      }
    } catch { /* silent */ }
  };

  const handleUseMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setLat(pos.coords.latitude.toFixed(4));
        setLon(pos.coords.longitude.toFixed(4));
        setLocationName("My Location");
      });
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/datacenter/generate`, {
        params: { data_type: dataType, lat, lon, start_date: startDate, end_date: endDate, include_forecast: forecast },
        withCredentials: true,
      });
      setDataset(res.data);
      setGenerated(true);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const chartData = useMemo(() => {
    if (!dataset) return [];
    return [
      ...dataset.data.map((d) => ({ ...d, type: "observed" })),
      ...dataset.forecast.map((f) => ({ date: f.date, forecast: f.value, type: "forecast" })),
    ];
  }, [dataset]);

  const downloadUrl = (format) =>
    `${API}/datacenter/download-${format}?data_type=${dataType}&lat=${lat}&lon=${lon}&start_date=${startDate}&end_date=${endDate}&include_forecast=${forecast}&location_name=${encodeURIComponent(locationName)}`;

  const s = dataset?.summary;

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="datacenter-page">
      {/* Header */}
      <header className="h-14 bg-stone-900/95 border-b border-stone-800 flex items-center px-4 gap-3 sticky top-0 z-50">
        <Link to="/dashboard" className="text-stone-400 hover:text-white"><ArrowLeft size={18} weight="bold" /></Link>
        <Database size={18} weight="bold" className="text-sky-400" />
        <span className="font-heading text-sm font-semibold">Data Center</span>
        <span className="text-[9px] text-stone-500 bg-stone-800 px-2 py-0.5 rounded">Professional Data Export</span>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* ─── Controls Panel ─── */}
        <div className="bg-stone-900 rounded-2xl border border-stone-800 p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Data Type Selector */}
            <div>
              <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-2 font-body">Select Data Type</label>
              <div className="grid grid-cols-2 gap-1.5">
                {DATA_TYPES.map((t) => (
                  <button key={t.id} onClick={() => setDataType(t.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-body transition-all ${
                      dataType === t.id
                        ? `${t.bg} border border-current`
                        : "bg-stone-800 border border-stone-700 text-stone-400 hover:border-stone-600"
                    }`}
                    style={dataType === t.id ? { color: t.color } : {}}
                    data-testid={`type-${t.id}`}>
                    <t.Icon size={14} weight="bold" /> {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-2 font-body">Location</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5">
                    <MagnifyingGlass size={14} className="text-stone-500 mr-2" />
                    <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      placeholder="Search city..." className="bg-transparent text-white text-xs w-full focus:outline-none"
                      data-testid="dc-search" />
                  </div>
                  <button onClick={handleUseMyLocation}
                    className="bg-stone-800 border border-stone-700 rounded-lg px-2 text-stone-400 hover:text-white hover:border-stone-600"
                    title="Use my location" data-testid="dc-my-location">
                    <Crosshair size={16} />
                  </button>
                </div>
                <div className="bg-stone-800/60 rounded-lg px-3 py-2 border border-stone-700/50">
                  <p className="text-xs text-white">{locationName}</p>
                  <p className="text-[10px] text-stone-500">{lat}, {lon}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="Latitude"
                    className="bg-stone-800 border border-stone-700 rounded-lg px-2 py-1.5 text-[11px] text-white focus:outline-none"
                    data-testid="dc-lat" />
                  <input value={lon} onChange={(e) => setLon(e.target.value)} placeholder="Longitude"
                    className="bg-stone-800 border border-stone-700 rounded-lg px-2 py-1.5 text-[11px] text-white focus:outline-none"
                    data-testid="dc-lon" />
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-2 font-body">Date Range</label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {PRESETS.map((p) => (
                    <button key={p.label} onClick={() => { setStartDate(daysAgo(p.days)); setEndDate(daysAgo(1)); }}
                      className="text-[10px] bg-stone-800 border border-stone-700 px-2 py-1 rounded text-stone-400 hover:text-white hover:border-stone-600 transition-colors"
                      data-testid={`preset-${p.days}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[9px] text-stone-600 block mb-0.5">Start</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-stone-800 border border-stone-700 rounded-lg px-2 py-1.5 text-[11px] text-white focus:outline-none"
                      data-testid="dc-start" />
                  </div>
                  <div>
                    <label className="text-[9px] text-stone-600 block mb-0.5">End</label>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-stone-800 border border-stone-700 rounded-lg px-2 py-1.5 text-[11px] text-white focus:outline-none"
                      data-testid="dc-end" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={forecast} onChange={(e) => setForecast(e.target.checked)}
                    className="w-4 h-4 accent-emerald-500 rounded" data-testid="dc-forecast-check" />
                  <span className="text-xs text-stone-300">Include 7-day forecast</span>
                </label>
              </div>
            </div>

            {/* Generate */}
            <div className="flex flex-col justify-end">
              <button onClick={handleGenerate} disabled={loading || !lat || !lon}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white font-medium py-3 rounded-xl text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="dc-generate">
                {loading ? <CircleNotch size={18} className="animate-spin" /> : <ChartLineUp size={18} weight="bold" />}
                {loading ? "Generating..." : "Generate Data"}
              </button>
              <p className="text-[9px] text-stone-600 mt-2 text-center">
                {activeType?.label} data for {locationName}
              </p>
            </div>
          </div>
        </div>

        {/* ─── Results ─── */}
        <AnimatePresence>
          {generated && dataset && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {[
                  { label: activeType?.label, value: `${s?.total_days} days`, color: "text-white" },
                  { label: "Average", value: `${s?.average} ${activeType?.unit}`, color: "text-sky-400" },
                  { label: "Minimum", value: `${s?.minimum} ${activeType?.unit}`, color: "text-blue-400" },
                  { label: "Maximum", value: `${s?.maximum} ${activeType?.unit}`, color: "text-red-400" },
                  { label: "Location", value: locationName.split(",")[0], color: "text-stone-300" },
                  { label: "Forecast", value: forecast ? "+7 days" : "Off", color: forecast ? "text-amber-400" : "text-stone-500" },
                ].map((st) => (
                  <div key={st.label} className="bg-stone-900 rounded-xl p-3 border border-stone-800">
                    <p className="text-[10px] text-stone-500 uppercase tracking-wider">{st.label}</p>
                    <p className={`text-lg font-heading font-semibold ${st.color}`}>{st.value}</p>
                  </div>
                ))}
              </div>

              {/* Chart */}
              <div className="bg-stone-900 rounded-2xl border border-stone-800 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-white">
                      {activeType?.label} — {locationName}
                    </h3>
                    <p className="text-[10px] text-stone-500">{startDate} to {endDate}{forecast ? " + 7-day forecast" : ""}</p>
                  </div>
                  <div className="flex gap-1">
                    {[
                      { id: "line", Icon: ChartLineUp, label: "Line" },
                      { id: "bar", Icon: ChartBarIcon, label: "Bar" },
                      { id: "area", Icon: Eye, label: "Area" },
                    ].map((c) => (
                      <button key={c.id} onClick={() => setChartType(c.id)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors ${
                          chartType === c.id ? "bg-sky-600/20 text-sky-400" : "text-stone-500 hover:text-stone-300"
                        }`} data-testid={`chart-${c.id}`}>
                        <c.Icon size={12} /> {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                  {chartType === "bar" ? (
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
                      <XAxis dataKey="date" tick={{ fill: "#78716c", fontSize: 9 }} axisLine={{ stroke: "#44403c" }} tickFormatter={(v) => v?.slice(5)} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: "#78716c", fontSize: 10 }} axisLine={{ stroke: "#44403c" }} />
                      <Tooltip contentStyle={{ background: "#1c1917", border: "1px solid #44403c", borderRadius: "8px", fontSize: "11px", color: "#fff" }}
                        formatter={(v, name) => [v, name === "forecast" ? "Forecast" : activeType?.label]} labelFormatter={(l) => `Date: ${l}`} />
                      <Bar dataKey="value" fill={activeType?.color} radius={[2, 2, 0, 0]} opacity={0.8} />
                      <Bar dataKey="forecast" fill="#f59e0b" radius={[2, 2, 0, 0]} opacity={0.6} />
                    </BarChart>
                  ) : chartType === "area" ? (
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="dcGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={activeType?.color} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={activeType?.color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
                      <XAxis dataKey="date" tick={{ fill: "#78716c", fontSize: 9 }} axisLine={{ stroke: "#44403c" }} tickFormatter={(v) => v?.slice(5)} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: "#78716c", fontSize: 10 }} axisLine={{ stroke: "#44403c" }} />
                      <Tooltip contentStyle={{ background: "#1c1917", border: "1px solid #44403c", borderRadius: "8px", fontSize: "11px", color: "#fff" }} />
                      <ReferenceLine y={s?.average} stroke={activeType?.color} strokeDasharray="4 4" strokeOpacity={0.4} />
                      <Area type="monotone" dataKey="value" stroke={activeType?.color} fill="url(#dcGrad)" strokeWidth={1.5} dot={false} />
                      <Area type="monotone" dataKey="forecast" stroke="#f59e0b" fill="none" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                    </AreaChart>
                  ) : (
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
                      <XAxis dataKey="date" tick={{ fill: "#78716c", fontSize: 9 }} axisLine={{ stroke: "#44403c" }} tickFormatter={(v) => v?.slice(5)} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: "#78716c", fontSize: 10 }} axisLine={{ stroke: "#44403c" }} />
                      <Tooltip contentStyle={{ background: "#1c1917", border: "1px solid #44403c", borderRadius: "8px", fontSize: "11px", color: "#fff" }} />
                      <ReferenceLine y={s?.average} stroke={activeType?.color} strokeDasharray="4 4" strokeOpacity={0.4} label={{ value: "Avg", fill: "#78716c", fontSize: 9 }} />
                      <Line type="monotone" dataKey="value" stroke={activeType?.color} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="forecast" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 3" dot={false} />
                    </LineChart>
                  )}
                </ResponsiveContainer>

                <div className="flex items-center gap-4 mt-3 px-2">
                  <span className="flex items-center gap-1.5 text-[10px] text-stone-400">
                    <span className="w-4 h-0.5 rounded" style={{ backgroundColor: activeType?.color }} /> {activeType?.label}
                  </span>
                  {forecast && (
                    <span className="flex items-center gap-1.5 text-[10px] text-stone-400">
                      <span className="w-4 h-0.5 bg-amber-500 rounded" style={{ borderTop: "2px dashed #f59e0b" }} /> Forecast
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-[10px] text-stone-400">
                    <span className="w-4 h-px rounded" style={{ borderTop: `1px dashed ${activeType?.color}80` }} /> Average
                  </span>
                </div>
              </div>

              {/* Download */}
              <div className="bg-stone-900 rounded-2xl border border-stone-800 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-white">Download Report</h3>
                    <p className="text-[10px] text-stone-500">
                      {s?.total_days} days of {activeType?.label} data{forecast ? " + 7-day forecast" : ""}. PDF is a journal-grade research manuscript with abstract, methodology, references and full appendix.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a href={downloadUrl("csv")} download
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 rounded-xl text-sm font-medium transition-colors"
                    data-testid="dc-download-csv">
                    <FileCsv size={18} weight="bold" /> Download CSV
                  </a>
                  <a href={downloadUrl("pdf")} download
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-xl text-sm font-medium transition-colors"
                    data-testid="dc-download-pdf">
                    <FileText size={18} weight="bold" /> Download PDF
                  </a>
                  <a href={downloadUrl("json")} download
                    className="flex items-center gap-2 bg-stone-700 hover:bg-stone-600 text-white px-5 py-3 rounded-xl text-sm font-medium transition-colors"
                    data-testid="dc-download-json">
                    <DownloadSimple size={18} weight="bold" /> Download JSON
                  </a>
                </div>

                <div className="mt-4 bg-stone-800/30 rounded-xl p-4 border border-stone-700/30">
                  <p className="text-[10px] text-stone-400 font-body leading-relaxed">
                    <strong className="text-stone-200">CSV:</strong> Tabular data with header block, summary statistics, data dictionary, and methodology notes. Best for Excel/Google Sheets and downstream analysis.<br/>
                    <strong className="text-emerald-400">PDF (Journal Manuscript):</strong> Typeset as a peer-reviewed Earth-science research article — title page with authors/affiliations, DOI, structured abstract, two-column body with numbered sections (Introduction, Study Area, Materials &amp; Methods, Results, Discussion, Forecast, Limitations, Conclusions), Figure 1, Table 1, References, Acknowledgements, CRediT author contributions, Data Availability, and a full daily-record Appendix. Suitable for academic citation, regulatory submission, and expert review.<br/>
                    <strong className="text-stone-200">JSON:</strong> Raw structured payload with full metadata, provenance, and FAIR-compliant data dictionary. Best for developers and pipelines.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty state */}
        {!generated && (
          <div className="text-center py-20">
            <Database size={48} className="text-stone-700 mx-auto mb-4" />
            <p className="text-stone-400 font-heading text-lg">Select your parameters and generate data</p>
            <p className="text-xs text-stone-600 mt-2 max-w-md mx-auto">
              Choose a data type, location, and date range above. The system will generate time-series data
              with optional 7-day forecasting. Download in CSV, PDF, or JSON format.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
